import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import {
  ArrowRight, Trophy, Briefcase, MessageCircle, Sparkles, BookOpen, Users,
  ChevronLeft, ChevronRight, ExternalLink, Star, Lock, PlayCircle, BookMarked,
  Rocket, Target, Send, TrendingUp, Compass, Handshake, Megaphone, ShieldCheck, Award, CalendarClock, Package, Medal, Crown, GraduationCap, Play, BarChart3,
} from "lucide-react";
import { api, fileUrl, apiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Loader } from "@/components/common";
import { LogoLockup } from "@/components/Logo";
import Reveal from "@/components/Reveal";
import { googleLoginRedirect } from "@/pages/Auth";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { TAGLINES } from "@/pages/OurStory";

const PHASE_IMAGES = [
  "https://static.prod-images.emergentagent.com/jobs/55c3455d-a519-4165-96df-a369a82e8d74/images/6e700c5b3f55eec62332d3e99a093028acc73abd6afc2ac91b4fa122923db78c.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/55c3455d-a519-4165-96df-a369a82e8d74/images/38659bb919f32941a1a1d3522af1d4e38ede3035717b547a13cd5ff629bdfa7a.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/55c3455d-a519-4165-96df-a369a82e8d74/images/7d06d8d76c2d7b513d818aaf1be679387490817565da7a316798089825d54b07.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/55c3455d-a519-4165-96df-a369a82e8d74/images/d4da6273edcd5d7fb57dab5b297c6e54035ff93421b436751e41c9be20aa4e16.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/55c3455d-a519-4165-96df-a369a82e8d74/images/c543a528aa892f0e3ed88bb11b3735ac68cad413f5441520886af3423cbca77b.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/55c3455d-a519-4165-96df-a369a82e8d74/images/a6e9c6ed62281e4610ff3d406d6e468d251697a59acd20a476e2ef66f0b404c2.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/55c3455d-a519-4165-96df-a369a82e8d74/images/e304b2e949d5c6e620e2fd17b3a5c30c4a41512f140cff89b45a4a4b12eede3e.jpeg",
];

let _sectionIndex = 0;
const Section = ({ children, className = "" }) => {
  const dir = _sectionIndex++ % 2 === 0 ? "left" : "right";
  return <Reveal direction={dir}><section className={`py-10 sm:py-14 ${className}`}>{children}</section></Reveal>;
};

function BannerCarousel({ banners }) {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true });
  useEffect(() => {
    if (!embla) return;
    const id = setInterval(() => embla.scrollNext(), 5000);
    return () => clearInterval(id);
  }, [embla]);
  return (
    <div className="overflow-hidden rounded-3xl border border-border shadow-sm" ref={emblaRef} data-testid="banner-carousel">
      <div className="flex">
        {banners.map((b) => (
          <a key={b.id} href={b.link || undefined} target={b.link ? "_blank" : undefined} rel="noreferrer"
            className="flex-[0_0_100%] min-w-0 relative block">
            <img src={b.image_url?.startsWith("http") ? b.image_url : fileUrl(b.image_url)} alt={b.title || ""} className="w-full h-48 sm:h-64 object-cover" />
            {(b.title || b.subtitle) && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex flex-col justify-end p-6">
                {b.title && <h3 className="text-white font-heading font-bold text-xl sm:text-2xl">{b.title}</h3>}
                {b.subtitle && <p className="text-white/90 text-sm mt-1">{b.subtitle}</p>}
              </div>
            )}
          </a>
        ))}
      </div>
    </div>
  );
}

function LiveCount({ start }) {
  const [n, setN] = useState(start || 0);
  useEffect(() => { setN(start || 0); }, [start]);
  useEffect(() => {
    let id;
    const tick = () => { setN((v) => v + (Math.random() < 0.5 ? 1 : 2)); id = setTimeout(tick, 4000 + Math.random() * 6000); };
    id = setTimeout(tick, 5000);
    return () => clearTimeout(id);
  }, []);
  return <>{n}+</>;
}

function AutoVideo({ src, className, style }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.play().catch(() => {}); } else { el.pause(); }
    }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, [src]);
  return <video ref={ref} src={src} muted loop playsInline preload="metadata" className={className} style={style} />;
}

function WinsCarousel({ wins }) {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "center" });
  const [selected, setSelected] = useState(0);
  const scrollTo = useCallback((i) => embla && embla.scrollTo(i), [embla]);
  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelected(embla.selectedScrollSnap());
    embla.on("select", onSelect); onSelect();
    const id = setInterval(() => embla.scrollNext(), 5000);
    return () => { clearInterval(id); embla.off("select", onSelect); };
  }, [embla]);

  return (
    <div data-testid="wins-carousel">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {wins.map((w) => (
            <div key={w.id} className="flex-[0_0_100%] min-w-0 px-2">
              <div className="bg-white border border-border rounded-2xl p-8 shadow-sm max-w-2xl mx-auto text-center">
                {w.photo_url ? (
                  <img src={fileUrl(w.photo_url)} alt={w.student_name} className="w-20 h-20 rounded-full object-cover mx-auto mb-4" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-teal/10 text-teal flex items-center justify-center text-2xl font-heading font-bold mx-auto mb-4">{w.student_name?.[0]}</div>
                )}
                <div className="flex justify-center gap-1 mb-3">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-warning text-warning" />)}</div>
                {w.achievement && <h3 className="font-heading text-lg font-semibold text-teal">{w.achievement}</h3>}
                {w.story && <p className="text-muted-foreground mt-2 leading-relaxed">"{w.story}"</p>}
                <p className="font-semibold mt-4">{w.student_name}</p>
                <p className="text-sm text-muted-foreground">{w.niche}{w.rate ? ` · ${w.rate}` : ""}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-center gap-2 mt-5">
        <button onClick={() => embla && embla.scrollPrev()} className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-secondary" data-testid="wins-prev"><ChevronLeft className="w-4 h-4" /></button>
        {wins.map((_, i) => (
          <button key={i} onClick={() => scrollTo(i)} data-testid={`wins-dot-${i}`}
            className={`h-2 rounded-full transition-all ${i === selected ? "w-6 bg-teal" : "w-2 bg-border"}`} />
        ))}
        <button onClick={() => embla && embla.scrollNext()} className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-secondary" data-testid="wins-next"><ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function HeroLoginCard() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(""), [password, setPassword] = useState(""), [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await login(email, password); toast.success("Welcome back!"); nav("/course"); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="relative bg-white rounded-3xl border border-emerald-100 shadow-xl shadow-emerald-900/5 p-7 sm:p-8" data-testid="hero-login-card">
      <div className="flex justify-center mb-4"><LogoLockup compact /></div>
      <h2 className="font-heading text-2xl font-bold text-center text-navy">Welcome back</h2>
      <p className="text-sm text-muted-foreground text-center mt-1">Log in to continue your journey</p>
      <form onSubmit={submit} className="space-y-4 mt-6">
        <div>
          <Label>Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="hero-login-email" />
        </div>
        <div>
          <Label>Password</Label>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="hero-login-password" />
        </div>
        <div className="text-right -mt-1"><Link to="/forgot-password" className="text-sm text-teal hover:underline" data-testid="hero-forgot-link">Forgot password?</Link></div>
        <Button type="submit" disabled={busy} className="w-full rounded-full bg-teal hover:bg-teal/90 text-white font-semibold h-11 shadow-sm shadow-teal/30" data-testid="hero-login-submit">Log In</Button>
      </form>
      <div className="flex items-center gap-3 my-4">
        <span className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">or</span><span className="h-px flex-1 bg-border" />
      </div>
      <Button type="button" variant="outline" className="w-full rounded-full h-11 font-semibold border-2" data-testid="hero-google-login"
        onClick={googleLoginRedirect}>
        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1a11 11 0 0 0-9.82 6.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
        Continue with Google
      </Button>
      <p className="text-center text-sm text-muted-foreground mt-5">
        New here? <Link to="/register" className="text-teal font-semibold hover:underline" data-testid="hero-goto-register">Create an account</Link>
      </p>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [taglineIdx, setTaglineIdx] = useState(0);
  const [showLogin, setShowLogin] = useState(false);
  const [wallItem, setWallItem] = useState(null);

  useEffect(() => {
    api.get("/home").then((r) => setData(r.data)).catch(() => {});
    api.get("/my/progress-summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);
  useEffect(() => { const t = setInterval(() => setTaglineIdx((i) => (i + 1) % TAGLINES.length), 2500); return () => clearInterval(t); }, []);

  if (!data) return <Loader />;
  _sectionIndex = 0;
  const { hero, student_count, wins, faqs, jobs_preview, phases, featured_recommendation, recommended_tools, support_notice, coming_soon, team = [], leaderboard = [], mentorship_testimonials = [], banners = [], active_challenge, playbook_card, hero_card = {}, growth_graph = { lines: [], data: [] }, country_leaderboard = [] } = data;
  const returning = summary && summary.completed_lessons > 0;
  const heroImg = hero_card?.image_url ? (hero_card.image_url.startsWith("http") ? hero_card.image_url : process.env.REACT_APP_BACKEND_URL + hero_card.image_url) : hero.image;

  return (
    <div className="space-y-2">
      <Dialog open={showLogin} onOpenChange={setShowLogin}>
        <DialogContent className="max-w-md p-0 bg-transparent border-0 shadow-none" data-testid="hero-login-dialog">
          <HeroLoginCard />
        </DialogContent>
      </Dialog>
      {banners.length > 0 && <div className="pt-4"><BannerCarousel banners={banners} /></div>}
      {/* HERO */}
      <Section className="!pt-4">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="animate-fade-up">
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-bold tracking-wide" data-testid="hero-breadcrumb">
              {["LEARN", "APPLY", "GET HIRED", "GROW"].map((step, i) => (
                <React.Fragment key={step}>
                  <span className="text-teal bg-teal/10 px-2.5 py-1 rounded-full">{step}</span>
                  {i < 3 && <ChevronRight className="w-3.5 h-3.5 text-teal/40" />}
                </React.Fragment>
              ))}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mt-5 text-navy" data-testid="hero-headline">{hero.headline}</h1>
            <blockquote className="mt-5 border-l-4 border-teal pl-4 italic text-navy/80" data-testid="hero-quote">
              "Your journey to remote work freedom starts with real skills and authentic guidance."
              <span className="block not-italic text-sm font-semibold text-teal mt-1">— {hero.coach_name || "Coach Reyland"}</span>
            </blockquote>
            <p className="text-base sm:text-lg text-muted-foreground mt-4 max-w-xl">{hero.tagline}</p>
            <div className="mt-6 space-y-3" data-testid="hero-bullets">
              {[
                { icon: BookOpen, label: "Learn In-Demand Skills" },
                { icon: Briefcase, label: "Get Real Opportunities" },
                { icon: Rocket, label: "Build Your Freedom" },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal to-brandgreen text-white flex items-center justify-center shrink-0 shadow-sm"><b.icon className="w-4.5 h-4.5" /></span>
                  <span className="font-semibold text-navy">{b.label}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-7" data-testid="hero-ctas">
              <Button size="lg" className="rounded-full bg-navy hover:bg-navy/90 h-12 pl-2 pr-6 text-base font-semibold text-white gap-2.5 shadow-md shadow-navy/20" data-testid="hero-start-free" onClick={() => nav("/course")}>
                <span className="w-8 h-8 rounded-full bg-teal flex items-center justify-center shrink-0"><Play className="w-4 h-4 fill-white text-white ml-0.5" /></span>
                Start Free Course <ArrowRight className="w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full h-12 px-7 text-base font-semibold border-2 border-navy/20 text-navy hover:bg-navy/5 bg-transparent" data-testid="hero-explore" onClick={() => nav("/products")}>
                Explore Courses
              </Button>
            </div>
            {!user && (
              <p className="text-sm text-muted-foreground mt-4" data-testid="hero-login-prompt">
                Already enrolled? <button onClick={() => setShowLogin(true)} className="text-teal font-semibold hover:underline" data-testid="hero-open-login">Log in</button>
              </p>
            )}
            <div className="mt-8 flex items-stretch rounded-2xl bg-white border border-emerald-100 shadow-sm divide-x divide-border overflow-hidden" data-testid="hero-stats">
              <div className="flex items-center gap-2 sm:gap-3 px-2.5 sm:px-5 py-4 flex-1 min-w-0">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-teal shrink-0" strokeWidth={1.75} />
                <div className="min-w-0"><p className="font-heading font-bold text-navy text-base sm:text-lg leading-none" data-testid="live-student-count"><LiveCount start={student_count} /></p><p className="text-[11px] sm:text-xs text-muted-foreground mt-1 leading-tight">Students Learning</p></div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 px-2.5 sm:px-5 py-4 flex-1 min-w-0">
                <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-teal shrink-0" strokeWidth={1.75} />
                <div className="min-w-0"><p className="font-heading font-bold text-navy text-[13px] sm:text-sm leading-tight">Real Opportunities</p><p className="text-[11px] sm:text-xs text-muted-foreground mt-1 leading-tight">Skills that lead to clients</p></div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 px-2.5 sm:px-5 py-4 flex-1 min-w-0">
                <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-teal shrink-0" strokeWidth={1.75} />
                <div className="min-w-0"><p className="font-heading font-bold text-navy text-[13px] sm:text-sm leading-tight">Proven Strategies</p><p className="text-[11px] sm:text-xs text-muted-foreground mt-1 leading-tight">Step-by-step guidance</p></div>
              </div>
            </div>
          </div>

          <div className="relative animate-fade-up w-full max-w-[420px] mx-auto lg:mx-0 lg:ml-auto" data-testid="hero-card">
            <div className="absolute -inset-4 bg-gradient-to-tr from-teal/15 to-brandgreen/15 rounded-[2.5rem] blur-2xl" aria-hidden />
            <div className="relative">
              <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] max-h-[460px] bg-gradient-to-b from-teal/5 to-brandgreen/5 shadow-lg">
                {heroImg && <img src={heroImg} alt={hero_card.name} className="w-full h-full object-cover" style={{ objectPosition: `${hero_card.pos_x ?? 50}% ${hero_card.pos_y ?? 28}%`, transform: `scale(${hero_card.zoom || 1})` }} data-testid="hero-image" />}
                <div className="absolute top-4 right-4 flex items-center gap-2.5 bg-white/95 backdrop-blur rounded-full pl-2.5 pr-4 py-2 shadow-md" data-testid="hero-badge">
                  <span className="w-8 h-8 rounded-full bg-teal/10 flex items-center justify-center shrink-0"><GraduationCap className="w-[18px] h-[18px] text-teal" /></span>
                  <div className="leading-tight">
                    <p className="text-[13px] font-bold text-navy">{hero_card.badge_title}</p>
                    <p className="text-[11px] text-muted-foreground">{hero_card.badge_subtitle}</p>
                  </div>
                </div>
              </div>
              <div className="text-center mt-5" data-testid="hero-card-name">
                <p className="font-heading text-2xl font-bold text-navy">{hero_card.name}</p>
                <p className="text-sm text-teal font-semibold mt-0.5">{hero_card.subtitle}</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* GROWTH GRAPH + COUNTRY LEADERBOARD */}
      <Section className="bg-navy -mx-4 sm:-mx-6 px-4 sm:px-6 rounded-3xl overflow-hidden relative">
        <div className="absolute -top-20 -right-16 w-72 h-72 decor-blob-teal rounded-full blur-3xl opacity-40" aria-hidden />
        <div className="grid lg:grid-cols-5 gap-6 relative z-10">
          <div className="lg:col-span-3">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold !text-white">The Upwork VA Opportunity</h2>
            <p className="text-white/60 text-sm mt-1 mb-4">Demand, the 4 major factors, and real student progress over time.</p>
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 h-[300px]" data-testid="growth-graph">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={growth_graph.data} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="name" stroke="rgba(255,255,255,0.5)" fontSize={12} />
                  <YAxis stroke="rgba(255,255,255,0.5)" fontSize={12} />
                  <RTooltip contentStyle={{ background: "#0a2230", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, color: "#fff" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {(growth_graph.lines || []).map((ln) => (
                    <Line key={ln.key} type="monotone" dataKey={ln.key} name={ln.label} stroke={ln.color} strokeWidth={2.5} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="lg:col-span-2">
            <h3 className="font-heading text-xl font-bold !text-white flex items-center gap-2"><Trophy className="w-5 h-5 text-teal-soft" /> Top Students by Country</h3>
            <div className="mt-4 space-y-2" data-testid="country-leaderboard">
              {country_leaderboard.map((c, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? "bg-amber-400 text-navy" : i === 1 ? "bg-slate-300 text-navy" : i === 2 ? "bg-orange-400 text-navy" : "bg-white/10 text-white"}`}>{i + 1}</span>
                  <span className="flex-1 text-white font-medium">{c.country}</span>
                  <span className="text-teal-soft font-heading font-bold">{c.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* CONTINUE LEARNING */}
      {returning && summary?.current_phase && (
        <Section>
          <div className="bg-white border border-border rounded-2xl p-6 sm:p-8 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="continue-learning">
            <div>
              <p className="text-sm text-teal font-semibold">Continue where you left off</p>
              <h3 className="font-heading text-xl font-bold mt-1">{summary.current_phase.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{summary.completed_lessons} of {summary.total_lessons} lessons completed</p>
            </div>
            <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={() => nav("/course")} data-testid="continue-btn">Resume <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
          </div>
        </Section>
      )}

      {/* PROMO CARDS */}
      <Section>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="group relative rounded-2xl p-7 overflow-hidden premium-card" data-testid="promo-niche">
            <span className="absolute top-5 right-5 text-xs font-semibold bg-teal text-white px-3 py-1 rounded-full">Coming Soon</span>
            <div className="w-12 h-12 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-105"><Package className="w-6 h-6 text-teal-soft" /></div>
            <h3 className="font-heading text-xl font-bold !text-white">Find Your Niche</h3>
            <p className="text-white/70 mt-2">Additional standalone niche courses (SMM, bookkeeping, design and more) are on the way.</p>
            <Button variant="ghost" className="rounded-full text-teal-soft hover:text-white hover:bg-white/10 mt-4 -ml-3" onClick={() => nav("/products")} data-testid="promo-niche-btn">Preview <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
          <div className="group relative rounded-2xl p-7 overflow-hidden premium-card" data-testid="promo-mentorship">
            <div className="w-12 h-12 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-105"><CalendarClock className="w-6 h-6 text-teal-soft" /></div>
            <h3 className="font-heading text-xl font-bold !text-white">Book a Mentorship Session</h3>
            <p className="text-white/70 mt-2">Get personalized 1-on-1 guidance from Coach Reyland — pick a live time slot today.</p>
            <Button className="rounded-full bg-teal text-white hover:bg-teal/90 mt-4" onClick={() => nav("/mentorship")} data-testid="promo-mentorship-btn">Book Now <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      </Section>

      {/* PREMIUM PLAYBOOK CARD */}
      {playbook_card?.enabled && (
        <Section>
          <div className="group relative rounded-[2rem] overflow-hidden min-h-[360px] sm:min-h-[460px] shadow-2xl transition-transform duration-300 hover:-translate-y-1.5" data-testid="playbook-card">
            {playbook_card.video_url
              ? <AutoVideo src={playbook_card.video_url.startsWith("http") ? playbook_card.video_url : process.env.REACT_APP_BACKEND_URL + playbook_card.video_url} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: `${playbook_card.pos_x ?? 50}% ${playbook_card.pos_y ?? 20}%`, transform: `scale(${playbook_card.zoom || 1})` }} />
              : playbook_card.image_url
              ? <img src={playbook_card.image_url.startsWith("http") ? playbook_card.image_url : process.env.REACT_APP_BACKEND_URL + playbook_card.image_url} alt="The Upwork Playbook by Coach Reyland" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: `${playbook_card.pos_x ?? 50}% ${playbook_card.pos_y ?? 50}%`, transform: `scale(${playbook_card.zoom || 1})` }} />
              : <div className="absolute inset-0 bg-gradient-to-br from-navy to-teal" />}
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/75 to-navy/25" />
            <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/30 to-transparent" />
            <div className="relative z-10 p-8 sm:p-14 flex flex-col justify-end min-h-[360px] sm:min-h-[460px]">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-teal/80 backdrop-blur px-3 py-1.5 rounded-full w-fit" data-testid="playbook-tag">{playbook_card.tag}</span>
              <h2 className="font-heading text-4xl sm:text-6xl font-extrabold !text-white mt-4 leading-[1.05] max-w-xl">The Upwork Playbook</h2>
              <p className="font-script text-4xl sm:text-5xl text-teal-soft mt-3" data-testid="playbook-script">{playbook_card.script_text || "Coach Reyland"}</p>
            </div>
          </div>
        </Section>
      )}

      {/* LEADERBOARD */}
      {(leaderboard.length > 0 || active_challenge) && (
        <Section>
          <div className="text-center max-w-2xl mx-auto mb-8">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange bg-orange/10 px-3 py-1.5 rounded-full"><Medal className="w-3.5 h-3.5" /> Leaderboard</span>
            <h2 className="font-heading text-3xl font-bold mt-3">Top Student Achievements</h2>
            <p className="text-muted-foreground mt-2">Celebrating the learners who are landing clients and building real careers.</p>
          </div>

          {active_challenge && (
            <div className="max-w-3xl mx-auto mb-6 rounded-2xl p-5 bg-gradient-to-r from-orange to-orange-soft text-white shadow-md flex items-center gap-4" data-testid="active-challenge">
              <Trophy className="w-8 h-8 shrink-0" />
              <div className="flex-1">
                <p className="font-heading font-bold text-lg">{active_challenge.title}</p>
                {active_challenge.prize && <p className="text-white/90 text-sm">Prize: {active_challenge.prize}{active_challenge.ends_at ? ` · Ends ${active_challenge.ends_at}` : ""}</p>}
                {active_challenge.criteria && <p className="text-white/80 text-xs mt-0.5">Eligibility: {active_challenge.criteria}</p>}
              </div>
            </div>
          )}

          {leaderboard[0] && (
            <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-md border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6 mb-6" data-testid="leaderboard-winner">
              <div className="relative shrink-0">
                <div className="absolute inset-0 flex items-center justify-center"><span className="text-[70px] leading-none">🌿</span></div>
                {leaderboard[0].photo_url ? (
                  <img src={fileUrl(leaderboard[0].photo_url)} alt={leaderboard[0].student_name} className="relative w-28 h-28 rounded-full object-cover ring-4 ring-warning/60" />
                ) : (
                  <div className="relative w-28 h-28 rounded-full bg-teal/10 text-teal flex items-center justify-center text-4xl font-heading font-bold ring-4 ring-warning/60">{leaderboard[0].student_name?.[0]}</div>
                )}
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-warning text-white flex items-center justify-center font-bold shadow">1</span>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="font-heading text-2xl font-bold">{leaderboard[0].student_name}</h3>
                <p className="text-muted-foreground mt-1">{leaderboard[0].achievement}</p>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-brandgreen bg-brandgreen/10 px-2.5 py-1 rounded-full mt-2"><ShieldCheck className="w-3.5 h-3.5" /> Verified Success</span>
              </div>
              {leaderboard[0].rate && (
                <div className="text-center bg-brandgreen/10 rounded-2xl px-6 py-4">
                  <p className="text-xs text-muted-foreground">Hourly Rate</p>
                  <p className="font-heading text-2xl font-extrabold text-brandgreen">{leaderboard[0].rate}</p>
                  <Trophy className="w-6 h-6 text-warning mx-auto mt-1" />
                </div>
              )}
            </div>
          )}

          {leaderboard.length > 1 && (
            <div className={`max-w-2xl mx-auto ${leaderboard.length > 20 ? "h-[420px] overflow-hidden relative" : ""}`} data-testid="leaderboard">
              <div className={leaderboard.length > 20 ? "animate-[ticker_30s_linear_infinite] space-y-2" : "space-y-2"}>
                {leaderboard.slice(1).map((l, i) => (
                  <div key={l.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm flex items-center gap-4" data-testid={`leader-${i + 1}`}>
                    <div className="w-9 h-9 rounded-xl bg-secondary text-muted-foreground flex items-center justify-center font-heading font-extrabold shrink-0">{i + 2}</div>
                    <div className="flex-1 min-w-0"><p className="font-semibold truncate">{l.student_name}</p><p className="text-sm text-muted-foreground truncate">{l.achievement}</p></div>
                    {l.rate && <span className="text-sm font-bold text-brandgreen shrink-0">{l.rate}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* STUDENT WINS */}
      <Section className="bg-white -mx-4 sm:-mx-6 px-4 sm:px-6 rounded-3xl">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brandgreen bg-brandgreen/10 px-3 py-1.5 rounded-full"><Trophy className="w-3.5 h-3.5" /> Student Wins</span>
          <h2 className="font-heading text-3xl font-bold mt-3">Real Results From Real Students</h2>
        </div>
        {wins.length > 0 ? <WinsCarousel wins={wins} /> : (
          <p className="text-center text-muted-foreground py-8" data-testid="wins-empty">Student wins will appear here as our community lands their first clients.</p>
        )}
      </Section>

      {/* FEATURED RECOMMENDATION + TOOLS */}
      {(featured_recommendation?.title || recommended_tools?.length > 0) && (
        <Section>
          <div className="grid md:grid-cols-2 gap-4">
            {featured_recommendation?.title && (
              <div className="bg-white border border-border rounded-2xl p-6 shadow-sm" data-testid="featured-recommendation">
                <span className="text-xs font-semibold text-teal">Coach Reyland Recommends</span>
                <h3 className="font-heading text-xl font-bold mt-1">{featured_recommendation.title}</h3>
                <p className="text-muted-foreground mt-2">{featured_recommendation.description}</p>
                {featured_recommendation.url && (
                  <a href={featured_recommendation.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-teal font-medium mt-4 hover:underline" data-testid="featured-link">
                    Learn More <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
            {recommended_tools?.map((t, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-6 shadow-sm" data-testid={`tool-${i}`}>
                <h3 className="font-heading text-lg font-semibold">{t.name}</h3>
                <p className="text-muted-foreground mt-2">{t.description}</p>
                {t.url && <a href={t.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-teal font-medium mt-4 hover:underline">Learn More <ExternalLink className="w-4 h-4" /></a>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* JOB BOARD PREVIEW */}
      <Section>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-3xl font-bold">Latest Opportunities</h2>
          <Button variant="ghost" className="rounded-full text-teal" onClick={() => nav("/jobs")} data-testid="view-all-jobs">View All Jobs <ArrowRight className="w-4 h-4 ml-1" /></Button>
        </div>
        {jobs_preview.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs_preview.map((j) => (
              <Link to={`/jobs/${j.id}`} key={j.id} className="bg-white border border-border rounded-2xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-transform" data-testid={`job-preview-${j.id}`}>
                {j.featured && <span className="text-xs font-semibold text-warning">★ Featured</span>}
                <h3 className="font-heading font-semibold mt-1">{j.title}</h3>
                <p className="text-sm text-teal font-medium mt-1">{j.rate}</p>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{j.summary}</p>
              </Link>
            ))}
          </div>
        ) : <p className="text-muted-foreground" data-testid="jobs-preview-empty">New hand-picked Upwork jobs are added regularly. Check back soon!</p>}
      </Section>

      {/* SUPPORT NOTICE */}
      <Section>
        <div className="bg-teal/5 border border-teal/20 rounded-2xl p-8 text-center" data-testid="support-notice">
          <MessageCircle className="w-10 h-10 text-teal mx-auto mb-3" />
          <h3 className="font-heading text-xl font-bold">{support_notice}</h3>
          <p className="text-muted-foreground mt-2 max-w-lg mx-auto">Have a question about the course or your progress? Our team is here to help.</p>
          <Button className="rounded-full bg-teal hover:bg-teal/90 mt-5" onClick={() => nav("/support")} data-testid="support-cta">Contact Support</Button>
        </div>
      </Section>

      {/* COMING SOON */}
      <Section>
        <h2 className="font-heading text-2xl font-bold text-center mb-6">Coming Soon</h2>
        <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
          {coming_soon.map((c, i) => (
            <div key={i} className="bg-white border border-dashed border-border rounded-2xl p-6 shadow-sm relative" data-testid={`coming-soon-${i}`}>
              <span className="absolute top-4 right-4 text-xs font-semibold bg-warning/10 text-warning px-2.5 py-1 rounded-full">{c.status}</span>
              {i === 0 ? <PlayCircle className="w-8 h-8 text-teal mb-3" /> : <BookMarked className="w-8 h-8 text-teal mb-3" />}
              <h3 className="font-heading font-semibold">{c.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{c.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* TEAM */}
      {team.length > 0 && (
        <Section className="bg-white -mx-4 sm:-mx-6 px-4 sm:px-6 rounded-3xl">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal bg-teal/10 px-3 py-1.5 rounded-full"><Users className="w-3.5 h-3.5" /> Meet The Team</span>
            <h2 className="font-heading text-3xl font-bold mt-3">The People Behind the Academy</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="team-grid">
            {team.map((t) => (
              <div key={t.id} className="text-center" data-testid={`team-${t.id}`}>
                {t.photo_url ? <img src={fileUrl(t.photo_url)} alt={t.name} className="w-24 h-24 rounded-2xl object-cover mx-auto shadow-sm" />
                  : <div className="w-24 h-24 rounded-2xl bg-teal/10 text-teal flex items-center justify-center text-3xl font-heading font-bold mx-auto">{t.name?.[0]}</div>}
                <p className="font-heading font-semibold mt-3">{t.name}</p>
                <p className="text-sm text-teal">{t.title}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* MENTORSHIP WALL */}
      {mentorship_testimonials.length > 0 && (
        <Section className="bg-navy -mx-4 sm:-mx-6 px-4 sm:px-6 rounded-3xl overflow-hidden relative">
          <div className="absolute -top-24 -left-24 w-80 h-80 decor-blob-teal rounded-full blur-3xl opacity-50" aria-hidden />
          <div className="absolute -bottom-24 -right-24 w-80 h-80 decor-blob-green rounded-full blur-3xl opacity-40" aria-hidden />
          <div className="text-center max-w-2xl mx-auto mb-10 relative z-10">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-soft bg-teal/15 px-3 py-1.5 rounded-full"><MessageCircle className="w-3.5 h-3.5" /> Mentorship Results</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-3 !text-white">What They Say About My Mentorship</h2>
            <p className="text-white/60 mt-2 text-sm">Real screenshots from students who trusted the process.</p>
          </div>
          <div className="marquee-wrap relative z-10" data-testid="mentorship-wall">
            <div className="marquee-track">
              {[...mentorship_testimonials, ...mentorship_testimonials].map((t, i) => (
                <button key={i} className="marquee-card group" onClick={() => setWallItem(t)} data-testid={`wall-card-${i}`}>
                  <img src={fileUrl(t.image_url)} alt={t.label || "Student feedback"} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/10 to-transparent opacity-80" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 text-left opacity-0 group-hover:opacity-100 transition-opacity">
                    {t.name && <p className="text-white text-sm font-semibold">{t.name}</p>}
                    {t.label && <p className="text-white/70 text-[11px] line-clamp-2">{t.label}</p>}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <Dialog open={!!wallItem} onOpenChange={(o) => !o && setWallItem(null)}>
            <DialogContent className="max-w-lg" data-testid="wall-popup">
              {wallItem && (
                <div>
                  <img src={fileUrl(wallItem.image_url)} alt="" className="w-full rounded-xl border border-border" />
                  <p className="font-heading font-bold text-navy mt-3" data-testid="wall-popup-name">{wallItem.name || "Student"}</p>
                  {wallItem.label && <p className="text-sm text-muted-foreground mt-1" data-testid="wall-popup-label">{wallItem.label}</p>}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </Section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <Section>
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl font-bold text-center mb-6">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {faqs.map((f) => (
                <AccordionItem key={f.id} value={f.id} className="bg-white border border-border rounded-2xl px-5">
                  <AccordionTrigger className="text-left font-medium hover:no-underline">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Section>
      )}

      <div className="text-center py-6">
        <Button variant="ghost" className="text-teal rounded-full" onClick={() => nav("/mentorship")} data-testid="mentorship-link">
          Want 1-on-1 help? Book a Mentorship session <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
